import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
} from 'recharts';

interface DataPoint {
  period: string;
  value: number;
}

interface ChartProps {
  title: string;
  xAxisName: string;
  yAxisName: string;
  data: DataPoint[];
  startColor?: string;
  endColor?: string;
  useKMBFormat?: boolean;
  chartPadding?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  tickFontSize?: number;
}

// Helper function to format numbers in K/M/B format
const formatNumberToKMB = (value: number): string => {
  const absValue = Math.abs(value);

  const format = (num: number, suffix: string) => {
    const formatted = num.toFixed(1);
    const trimmed = formatted.replace(/\.0$/, '');
    return trimmed + suffix;
  };

  if (absValue >= 1_000_000_000) {
    return format(value / 1_000_000_000, 'B');
  }
  if (absValue >= 1_000_000) {
    return format(value / 1_000_000, 'M');
  }
  if (absValue >= 1_000) {
    return format(value / 1_000, 'K');
  }
  return value.toLocaleString();
};

const CumulativeChart: React.FC<ChartProps> = ({
  title,
  xAxisName,
  yAxisName,
  data,
  startColor = '#0EA5E9',
  endColor = '#0EA5E9',
  useKMBFormat = false,
  chartPadding = {
    top: 50,
    right: 20,
    bottom: 20,
    left: 20,
  },
  tickFontSize = 12,
}) => {
  // Function to format numbers based on the useKMBFormat prop
  const formatValue = (value: number): string => {
    return useKMBFormat ? formatNumberToKMB(value) : value.toLocaleString();
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-neutral-900 border border-gray-700 shadow-xl p-4 rounded">
        <p className="text-gray-300">
          {xAxisName}: {label}
        </p>
        <p className="font-semibold text-gray-300">
          {yAxisName}: {formatValue(payload[0].value)}
        </p>
      </div>
    );
  };

  return (
    <div className="w-full h-[500px] bg-background-dark-subdued p-4 rounded-lg">
      <h2 className="font-semibold text-gray-100">{title}</h2>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: chartPadding.top,
            right: chartPadding.right,
            left: chartPadding.left,
            bottom: chartPadding.bottom,
          }}
        >
          <defs>
            <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="20%" stopColor={startColor} stopOpacity={0.5} />
              <stop offset="100%" stopColor={endColor} stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={true}
            horizontal={true}
            stroke="#374151"
            opacity={0.4}
          />
          <XAxis
            dataKey="period"
            tick={{
              fill: '#9CA3AF',
              fontSize: tickFontSize,
            }}
            tickLine={false}
            axisLine={{ stroke: '#4B5563' }}
            height={65}
          >
            <Label
              value={xAxisName}
              position="insideBottom"
              style={{
                fill: '#9CA3AF',
                textAnchor: 'middle',
              }}
            />
          </XAxis>
          <YAxis
            tick={{
              fill: '#9CA3AF',
              fontSize: tickFontSize,
            }}
            tickFormatter={formatValue}
            stroke="#4B5563"
            width={80}
          >
            <Label
              value={yAxisName}
              angle={-90}
              position="insideLeft"
              style={{
                fill: '#9CA3AF',
                textAnchor: 'middle',
              }}
            />
          </YAxis>
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={startColor}
            fill="url(#colorGradient)"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CumulativeChart;
